/**
 * src/components/ThemeSwitcher.jsx — dashboard mode picker.
 *
 * Lives in the sidebar footer (see App.jsx), not Settings — Settings is
 * owner-only, and an artist's own light/dark/system preference is their
 * own choice, not the studio's.
 *
 * The dashboard has one theme now ("Tattoo Labs" — see lib/theme.js), so
 * this no longer picks a theme, only a MODE. Each swatch still sets
 * `data-theme`/`data-mode` on itself so its preview colour comes from the
 * real generated CSS (design-tokens/themes.js) rather than a duplicated
 * value in JS — the System swatch deliberately gets no `data-mode`
 * attribute at all, so it resolves via the same `prefers-color-scheme`
 * media query the real page uses, showing exactly what "system" means on
 * this device right now.
 */
import { MODES, MODE_LABELS, setMode } from '../lib/theme.js';

/**
 * @param {object} props
 * @param {'light'|'dark'|'system'} props.value Currently active mode.
 * @param {(mode: string) => void} props.onChange Called after the mode has
 *   already been applied and saved to localStorage — for callers that also
 *   want to persist it elsewhere (e.g. the artist's account).
 * @returns {JSX.Element}
 */
export default function ThemeSwitcher({ value, onChange }) {
  return (
    <div className="theme-swatch-row" role="group" aria-label="Appearance">
      {MODES.map((mode) => {
        const selected = value === mode;
        const label = MODE_LABELS[mode] ?? mode;
        return (
          <button
            key={mode}
            type="button"
            data-theme="tattoo-labs"
            data-mode={mode === 'system' ? undefined : mode}
            className={`theme-swatch${selected ? ' selected' : ''}`}
            onClick={() => {
              setMode(mode);
              onChange?.(mode);
            }}
            title={label}
            aria-label={label}
            aria-pressed={selected}
          />
        );
      })}
    </div>
  );
}
